import { SyntheticEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import usePath from '../../../../../components/Hooks/usePath';
import { useModalState } from '../../../../../hooks/useModalState';
import LangEnum from '../../../../../lang/LangEnum';
import PATHS from '../../../../../routes/routes.const';
import { OrderProductError } from '../../../../../services/modules/order-product/order-product.types';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../../../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../../../../stores/ProductDetails/productDetails.store';
import { WithId } from '../../../../../types/Base.types';
import { useNetworkContext } from '../../../../offline/contexts/network/network.context';
import { isOfflineStatus } from '../../../../offline/domain/offlineOrder/offlineOrder.types';
import { AvailabilityEnum, ESProductColor } from '../../../../products/ProductTypes';
import { resolvePreferredProductColorId } from '../../../../products/products-details-v2/hooks/useOpenProductDetails.utils';
import { availabilityToCompactAvailability } from '../../../../products/utils/availability.utils';
import { useOrderContext } from '../../../contexts/Order.context';
import { useOrderProductsContext } from '../../../contexts/OrderProducts.context';
import { OrderStatus } from '../../../interfaces/order-status.interface';
import { StatusType } from '../../../order.types';
import {
  OrderProduct,
  OrderProductGridItem,
  OrderProductKeyIds,
  Section,
} from '../../orderProducts.types';
import { getProductSectionIndex } from '../../orderProducts.utils';
import useRelatedMaterials from '../grid/components/orderProductGrid/hooks/useRelatedMaterials';
import { HandleSelectItemNewDeliveryWindow } from '../types/useOrderProductItem.types';

interface UseOrderProductItemProps {
  productIndex: number;
  sections: Section[];
  sectionIndex: number;
  isLastSection: boolean;
  product: OrderProduct;
  grid: OrderProductGridItem[];
  productErrors?: OrderProductError[];
}

function useOrderProductItem({
  productIndex,
  sections,
  sectionIndex,
  isLastSection,
  grid,
  product,
  productErrors,
}: UseOrderProductItemProps) {
  const params = useParams<WithId>();
  const { selectedOrder } = useOrderBookStore(
    useShallow((state) => ({ selectedOrder: state.selectedOrder })),
  );
  const { isPath } = usePath();
  const { order, removeCopiedGrid } = useOrderContext();
  const { getRelatedProductColorIds, refetchProductColors } = useRelatedMaterials();
  const {
    onSelectNewColor,
    onClickRemove,
    onSelectNewDeliveryWindow,
    onClickRemoveKitFromOrder,
    isLoadingRequest: isDeletingKit,
    sellout,
  } = useOrderProductsContext();

  const isOrderProductFromKit = !!product.kit && !!product.kit.id;
  const {
    isOpen: isConfirmRemoveModalOpen,
    openModal: openConfirmRemoveModal,
    closeModal: closeConfirmRemoveModal,
  } = useModalState();

  const { isForcingOfflineMode } = useNetworkContext();
  const isOfflineOrderInOnlineMode = !isForcingOfflineMode && isOfflineStatus(order?.status);

  const orderId = params.id;

  const isLastProductSection =
    getProductSectionIndex({ productIndex, sectionIndex, sections }) ===
    sections[sectionIndex].products.length - 1;

  const isLastOrderProduct = isLastProductSection && isLastSection;

  const { productNotificationState, notificationTimerIsValid, resetProductActionsStore } =
    useProductActionsStore(
      useShallow((state) => ({
        productNotificationState: state.productNotificationState,
        notificationTimerIsValid: state.notificationTimerIsValid,
        resetProductActionsStore: state.resetProductActionsStore,
      })),
    );

  const productAddedByActions =
    productNotificationState?.kitId === product.kit?.id ||
    productNotificationState?.productColorId === product.productColorId;
  const highlightProduct = productAddedByActions && notificationTimerIsValid;

  const statusTypes: Partial<OrderStatus>[] = [StatusType.ABANDONED, StatusType.CANCELED];
  const isNotAbandonedOrCanceledOrder =
    !!order?.status && !isOfflineStatus(order?.status) && !statusTypes.includes(order?.status);

  const handleSelectNewColor = useCallback(
    (productColor: ESProductColor) => {
      onSelectNewColor({ grid, productColor, productIndex, sectionIndex });
    },
    [grid, onSelectNewColor, productIndex, sectionIndex],
  );

  const handleSelectNewDeliveryWindow: HandleSelectItemNewDeliveryWindow = useCallback(
    ({ availability, lastAvailability, newDwAvailability }) =>
      onSelectNewDeliveryWindow({
        grid,
        newAvailability: availability,
        productIndex,
        sectionIndex,
        dwAvailability: newDwAvailability,
        lastAvailability,
      }),
    [grid, productIndex, onSelectNewDeliveryWindow, sectionIndex],
  );

  const handleRemove = useCallback(
    async (item: OrderProduct) => {
      const mustRefetchRelatedProductColors =
        item.availability === AvailabilityEnum.MATERIAL_PRE_ORDER;
      const relatedProductColorsBeforeDelete = mustRefetchRelatedProductColors
        ? await getRelatedProductColorIds(orderId, [item.productColorId])
        : [];

      resetProductActionsStore();
      removeCopiedGrid();
      await onClickRemove(item, sectionIndex);
      refetchProductColors(orderId, relatedProductColorsBeforeDelete);
    },
    [
      orderId,
      sectionIndex,
      onClickRemove,
      removeCopiedGrid,
      refetchProductColors,
      resetProductActionsStore,
      getRelatedProductColorIds,
    ],
  );

  const handleClickRemove = async (item: OrderProduct) => {
    if (isOrderProductFromKit) {
      openConfirmRemoveModal();
      return;
    }

    await handleRemove(item);
  };

  const {
    productGridParams,
    setFinishedOrder,
    setProductDetailsModalState,
    handleOpenProductDetailsModal,
  } = useProductDetailsStore(
    useShallow((productDetailsState) => ({
      productGridParams: productDetailsState.productGridParams,
      setProductDetailsModalState: productDetailsState.setProductDetailsModalState,
      handleOpenProductDetailsModal: productDetailsState.handleOpenProductDetailsModal,
      setFinishedOrder: productDetailsState.setFinishedOrder,
    })),
  );

  const getDetailsProductColorId = (isEditableOrder: boolean) =>
    isEditableOrder
      ? resolvePreferredProductColorId(product.productColorId)
      : product.productColorId;

  const handleClick = (event: SyntheticEvent) => {
    event.preventDefault();
    const isOrderEditPage = isPath(PATHS.main.orders.editForm.path);
    const isOrderSuggestedPage = isPath(PATHS.main.orders.suggestion.path);
    const isPublicOrderPage = isPath(PATHS.public.order.path);
    const isFinishedOrder = !isOrderEditPage && !isOrderSuggestedPage && !isPublicOrderPage;
    const currentOrderId = isFinishedOrder ? orderId : selectedOrder?.id;

    if (isOfflineOrderInOnlineMode) {
      return;
    }

    setFinishedOrder(isFinishedOrder ? order : undefined);
    if (isOrderProductFromKit && product.kit) {
      handleOpenProductDetailsModal({
        ...productGridParams,
        orderIds: currentOrderId ? [currentOrderId] : [],
        isFinishedOrder,
      });
      setProductDetailsModalState({
        isOpen: true,
        kitId: product.kit.id,
      });

      return;
    }

    handleOpenProductDetailsModal({
      ...productGridParams,
      availability: availabilityToCompactAvailability(product.availability),
      deliveryWindowIds: undefined,
      deliveryWindowsSnapshot: undefined,
      productColorId: getDetailsProductColorId(isOrderEditPage || isOrderSuggestedPage),
      orderIds: currentOrderId ? [currentOrderId] : [],
      isFinishedOrder,
    });

    setProductDetailsModalState({
      isOpen: true,
      productColorId: product.productColorId,
      productId: product.productId,
      customerId: order?.customer?.id,
    });
  };

  const orderProductKeyIds: OrderProductKeyIds = useMemo(
    () => ({
      productColorId: product.productColorId,
      availability: product.availability,
      deliveryWindowId: product.deliveryWindow?.id,
    }),
    [product.productColorId, product.availability, product.deliveryWindow?.id],
  );

  const packsWithError = productErrors?.find(
    (error) => error.messageCode === LangEnum.ORDER_PRODUCT_RULE_INVALID_PACK,
  )?.extra?.packsWithError;

  const packsWithUnavailableStock = productErrors?.find(
    (error) => error.messageCode === LangEnum.ORDER_PRODUCT_RULE_INVALID_PACK_STOCK,
  )?.extra?.packsWithError;

  const handleRemoveKitFromOrder = useCallback(async () => {
    if (product.kit) {
      await onClickRemoveKitFromOrder(product.kit.id);
      closeConfirmRemoveModal();
    }
  }, [onClickRemoveKitFromOrder, product.kit, closeConfirmRemoveModal]);

  const selloutSummary = sellout.getSummary(product.productColorId);

  return {
    handleClick,
    isDeletingKit,
    selloutSummary,
    packsWithError,
    highlightProduct,
    handleClickRemove,
    orderProductKeyIds,
    isLastOrderProduct,
    handleSelectNewColor,
    productAddedByActions,
    isOrderProductFromKit,
    closeConfirmRemoveModal,
    handleRemoveKitFromOrder,
    isConfirmRemoveModalOpen,
    packsWithUnavailableStock,
    isNotAbandonedOrCanceledOrder,
    handleSelectNewDeliveryWindow,
    isOfflineOrderInOnlineMode,
  };
}

export default useOrderProductItem;
